<template>
    <li class="dashboard-renovation">
        <div class="dashboard-renovation__primary">
            <div class="dashboard-renovation__primary__bullet" />
            <div class="dashboard-renovation__primary__main">
                <section class="dashboard-renovation__primary__main__list-bullet">
                    <i class="dashboard-renovation__primary__main__list-bullet__icon fas fa-rectangle-list" />
                </section>
                <section class="dashboard-renovation__primary__main__content-wrapper">
                    <h4 class="dashboard-renovation__primary__main__name">
                        {{ renovation.name }}
                    </h4>
                    <section class="dashboard-renovation__primary__main__core">
                        <div class="dashboard-renovation__primary__main__core__data-item dashboard-renovation__primary__main__core__data-item--status">
                            <span class="dashboard-renovation__primary__main__core__data-item__label">{{ myResources.statusLabelText }}</span>
                            <span class="dashboard-renovation__primary__main__core__data-item__value">{{ renovation.displayStatus }}</span>
                        </div>
                        <div class="dashboard-renovation__primary__main__core__data-item dashboard-renovation__primary__main__core__data-item--cost"
                             v-if="!scheme.priceless">
                            <span class="dashboard-renovation__primary__main__core__data-item__label">{{ myResources.costLabelText }}</span>
                            <span class="dashboard-renovation__primary__main__core__data-item__value">{{ renovation.cost }}</span>
                        </div>
                    </section>
                    <hh-btn class="dashboard-renovation__primary__main__toggle dashboard-renovation__primary__main__toggle--see-more"
                            :text="myResources.seeMoreButtonText"
                            type="link"
                            @click="toggleDetails"
                            v-if="!showDetail" />
                </section>
            </div>
            <section class="dashboard-renovation__primary__buttons">
                <hh-btn class="dashboard-renovation__primary__buttons__button dashboard-renovation__primary__buttons__button--edit"
                        :text="myResources.editButtonText"
                        :short-text="myResources.editShortButtonText"
                        :track-operation-state="true"
                        :operation-in-progress="planIsLoading"
                        @click="onEditClick" />
            </section>
        </div>
        <section class="dashboard-renovation__secondary"
                 v-if="showDetail">
            <div class="dashboard-renovation__secondary__bullet" />
            <div class="dashboard-renovation__secondary__list-bullet" />
            <div class="dashboard-renovation__secondary__main">
                <div class="dashboard-renovation__secondary__main__data-item dashboard-renovation__secondary__main__data-item--budget"
                     v-if="!scheme.priceless">
                    <span class="dashboard-renovation__secondary__main__data-item__label">{{ myResources.budgetLabelText }}</span>
                    <span class="dashboard-renovation__secondary__main__data-item__value">{{ renovation.budget }}</span>
                </div>
                <div class="dashboard-renovation__secondary__main__data-item dashboard-renovation__secondary__main__data-item--motivation">
                    <span class="dashboard-renovation__secondary__main__data-item__label">{{ myResources.motivationLabelText }}</span>
                    <span class="dashboard-renovation__secondary__main__data-item__value">{{ motivationDisplay }}</span>
                </div>
                <div class="dashboard-renovation__secondary__main__data-item dashboard-renovation__secondary__main__data-item--improvements">
                    <span class="dashboard-renovation__secondary__main__data-item__label">{{ myResources.improvementsLabelText }}</span>
                    <ul class="dashboard-renovation__secondary__main__data-item__list">
                        <li class="dashboard-renovation__secondary__main__data-item__list__item"
                            v-for="item in renovation.improvements"
                            :key="item.id">
                            {{ item.name }}
                        </li>
                    </ul>
                </div>
                <hh-btn class="dashboard-renovation__secondary__main__toggle dashboard-renovation__secondary__main__toggle--see-less"
                        :text="myResources.seeLessButtonText"
                        type="link"
                        @click="toggleDetails" />
            </div>
        </section>
    </li>
</template>

<script>

import Renovation from '@/models/renovation'

import ResourcesHelper from '@/components/resources-helper'

import PlanBuilderManager from '@/plan-builder-manager'
import ProgressManager from '@/progress-manager'
import PropertyManager from '@/property-manager'
import RenovationsManager from '@/renovations-manager'

export default {

    mixins: [
        ResourcesHelper,
    ],

    props: {

        /**
         * The renovation plan to display details for.
         * @type {Renovation}
         */
        renovation: {
            type: Renovation,
            required: true,
        },
    },

    data() {
        return {

            /**
             * @type {Boolean}
             */
            planIsLoading: false,

            /**
             * @type {Boolean}
             */
            showDetail: false,
        }
    },

    computed: {

        /**
         * @returns {string}
         */
        motivationDisplay() {
            const motivationResource = this.commonResources.motivations
                .find(mr => mr.key === this.renovation.motivation)

            return motivationResource ? motivationResource.shortText : 'Unknown'
        },

        /**
         * Return the resources object for this page.
         * @returns {DashboardRenovationResources}
         */
        myResources() {
            return this.parseResources('components.dashboard.renovation')
        },
    },

    methods: {

        /**
         * Switches the address and loads the plan into state so that the Plan Builder page can be directly loaded.
         * @returns {Promise<void>}
         * @private
         */
        async _preloadPropertyAndRenovationAsync() {
            ProgressManager.clearVisitedRoutes()
            const property = await PropertyManager.switchChosenPropertyAsync(this.renovation.addressId)
            this.$logger.logObject(property, `Property loaded with ID ${ this.renovation.addressId }`, 'Dashboard')
            await PropertyManager.loadHomeDataIfNeededOrForcedAsync(this.renovation.addressId, true)
            const loadedRenovation = await RenovationsManager.loadAsync(this.renovation.name)
            await PlanBuilderManager.loadProjectFromRenovationAsync(loadedRenovation)
        },

        /**
         * Triggers when clicking See More / Less.
         */
        toggleDetails() {
            this.showDetail = !this.showDetail
        },

        /**
         * Triggers when clicking Edit.
         */
        onEditClick() {
            this.planIsLoading = true
            this.$logger.log(`Loading renovation plan - ${ this.renovation.loggingString }`, 'Dashboard')
            this.$nextTick(async() => {
                await this._preloadPropertyAndRenovationAsync()
                this.$router.push({ name: 'PlanBuilder' })
            })
        },
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\_dashboard-renovation.scss

@mixin nested-data-item() {
    @include element('data-item') {
        flex: 1 0 49%;
        font-size: 0.9rem;
        white-space: nowrap;

        @include element('label') {
            font-weight: bold;

            &::after {
                content: ':';
            }
        }

        @include element('value') {
            margin-left: 0.3rem;
            padding-right: 1rem;
        }

        @include element('list') {
            @include styled-data-list;
            flex: 0 1 100%;
            text-align: left;

            > li {
                padding-left: 2.1rem;
                text-indent: -0.9rem;
                white-space: normal;

                @include bp-min-mid {
                    padding-left: 2.4rem;
                }

                @include bp-min-wide {
                    padding-left: 2.8rem;
                }
            }
        }
    }
}

@mixin nested-list-bullet() {
    $list-bullet-size-small: 2.5rem;
    $list-bullet-size-med: 3rem;
    $list-bullet-size-large: 3.5rem;

    @include element('list-bullet') {
        display: none;
        height: 1rem;
        flex: 0 0 $list-bullet-size-small;

        @include bp-min-mid {
            display: block;
            flex: 0 0 $list-bullet-size-med;
        }

        @include bp-min-wide {
            flex: 0 0 $list-bullet-size-large;
        }

        @include element('icon') {
            @include wrapped-icon-size($list-bullet-size-small);

            @include bp-min-mid {
                @include wrapped-icon-size($list-bullet-size-med);
            }

            @include bp-min-wide {
                @include wrapped-icon-size($list-bullet-size-large);
            }
        }
    }
}

.dashboard-renovation {

    @include element('primary') {
        @include bulleted-dashboard-layout-core();

        @include element('main') {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            @include nested-list-bullet();

            @include element('content-wrapper') {
                flex: 8 1 100%;
            }

            @include element('name') {
                font-size: 1rem;
                margin: 0;
            }

            @include element('core') {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                align-items: flex-start;
                min-width: 0;

                @include nested-data-item();
            }
        }

        @include nested-element('buttons', 'button') {
            @include bulleted-dashboard-button-widths;
        }
    }

    @include element('secondary') {
        @include bulleted-dashboard-layout-core();
        @include nested-list-bullet();

        @include element('main') {
            padding-top: 0;
            @include nested-data-item();
        }
    }
}

</style>
